import React from "react";
import { Chip, Stack } from "@mui/material";
import { QuickReply } from "../utils/types";
import { useHorizontalDragScroll } from "../hooks/useHorizontalDragScroll";

interface SuggestionChipsProps {
  chips: QuickReply[];
  onChipClick: (chip: QuickReply) => void;
}

const SuggestionChips: React.FC<SuggestionChipsProps> = ({ chips, onChipClick }) => {
  const { containerRef, isDragging, isOverflowing, dragHandlers, centerScroll } = useHorizontalDragScroll();
  const chipsSignature = React.useMemo(
    () => chips.map((chip) => `${chip.title}|${chip.payload ?? ""}`).join("||"),
    [chips]
  );

  React.useEffect(() => {
    const rafId = window.requestAnimationFrame(() => centerScroll());
    return () => window.cancelAnimationFrame(rafId);
  }, [centerScroll, chipsSignature]);

  const handleChipClick = (chip: QuickReply) => {
    // Check if the payload is a URL
    if (chip.payload && (chip.payload.startsWith('http://') || chip.payload.startsWith('https://'))) {
      window.open(chip.payload, '_blank');
    } else {
      onChipClick(chip);
    }
  };

  return (
    <div className="suggestion-chips-wrapper">
      <Stack
        ref={containerRef}
        direction="row"
        spacing={1}
        className={`suggestion-chips${isDragging ? " is-dragging" : ""}${isOverflowing ? " is-overflowing" : ""}`}
        sx={{ flexWrap: "nowrap", overflowX: "auto" }}
        {...dragHandlers}
      >
        {chips.map((chip, idx) => (
          <Chip
            key={idx}
            label={chip.title}
            onClick={() => handleChipClick(chip)}
            variant="outlined"
            clickable
              sx={{
                background: "rgba(255, 255, 255, 0.1)",
                boxShadow: "0 3px 10px rgba(0, 0, 6, 0.3)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                borderRadius: "16px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                color: "var(--text-dark)",
              }}
          />
        ))}
      </Stack>
    </div>
  );
};

export default SuggestionChips;
